import { useEffect } from 'react';
import { useNavigate, useRouteError, useLocation } from 'react-router-dom';
import { hapticsImpactLight } from './utils/haptics';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import { useRefreshStudySessionAndContent } from './utils/refreshStudySession';
import { getUserInfo } from './services/auth';
import { capture } from './utils/analytics';
import { classifyAssetLoadFailure } from './utils/assetLoadFailure';
import {
  MAX_DOM_COMMIT_RECOVERY_ATTEMPTS,
  getDomCommitRecoveryStorage,
  isUnrecoverableDomCommitError,
  readDomCommitRecoveryAttempts,
  writeDomCommitRecoveryAttempts,
} from './utils/unrecoverableDomCommitError';
import {
  addSentryBreadcrumb,
  captureSentryMessage,
  withSentryScope,
} from './utils/sentry';
import { Button } from './components/ui/button';
import AssetLoadErrorView from './components/ora/ErrorBoundary/AssetLoadErrorView';
import { triggerGuardedClientReload } from './services/clientVersion';

// Long enough for the Sentry capture that precedes it to reach the transport,
// short enough that the user reads it as a page load rather than a hang.
const DOM_COMMIT_RECOVERY_RELOAD_DELAY_MS = 150;

function isErrorWithStatus(
  error: unknown
): error is { status: number; statusText?: string; message?: string } {
  return typeof error === 'object' && error !== null && 'status' in error;
}

function isLikelyTrackedEmailScanner404(pathname: string): boolean {
  return /^\/\d+\/[A-Za-z0-9-]+=*$/.test(pathname);
}

function ErrorPage() {
  const error: unknown = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const { refreshStudySessionAndContent } = useRefreshStudySessionAndContent();

  const refreshAndReturnHome = async () => {
    await getUserInfo();
    await refreshStudySessionAndContent();
    navigate('/');
  };

  const escapeHatch = async () => {
    capture('hard_refresh_clicked');
    captureSentryMessage('Hard refresh clicked');
    localStorage.clear();
    sessionStorage.clear();

    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    });

    window.location.reload();
  };

  // Enhanced error logging to Sentry
  useEffect(() => {
    const logErrorToSentry = () => {
      const shouldIgnore404InSentry =
        isErrorWithStatus(error) &&
        error.status === 404 &&
        isLikelyTrackedEmailScanner404(location.pathname) &&
        !document.referrer &&
        !currentUser?.id &&
        !location.search &&
        !location.hash;

      if (shouldIgnore404InSentry) {
        if (process.env.NODE_ENV === 'development') {
          console.info(
            'Skipping likely email-scanner 404 in Sentry:',
            location.pathname
          );
        }
        return;
      }

      const timestamp = new Date().toISOString();
      const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const assetLoadFailure = classifyAssetLoadFailure(error);

      if (assetLoadFailure) {
        addSentryBreadcrumb({
          message: 'Recoverable asset-load failure reached route error page',
          level: 'warning',
          category: 'error-recovery',
          data: {
            kind: assetLoadFailure.kind,
            pathname: location.pathname,
          },
        });

        return;
      }

      // Set context for this error
      withSentryScope((scope, Sentry) => {
        // Set error ID for tracking
        scope.setTag('errorId', errorId);
        scope.setTag('errorSource', 'ErrorPage');
        scope.setTag('timestamp', timestamp);

        // Route context
        scope.setContext('route', {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          state: location.state,
          key: location.key,
        });

        // Browser context
        scope.setContext('browser', {
          userAgent: navigator.userAgent,
          url: window.location.href,
          referrer: document.referrer,
          timestamp: timestamp,
        });

        // App state context (keys only for privacy)
        scope.setContext('appState', {
          localStorageKeys: Object.keys(localStorage),
          sessionStorageKeys: Object.keys(sessionStorage),
          localStorageCount: localStorage.length,
          sessionStorageCount: sessionStorage.length,
        });

        // Error categorization
        const errorString = String(error);
        let errorCategory = 'unknown';
        let errorSubcategory = '';

        if (isErrorWithStatus(error)) {
          errorCategory = 'http';
          if (error.status === 404) {
            errorSubcategory = 'not-found';
            scope.setTag('errorType', '404');
            scope.setLevel('info'); // 404s are less critical
          } else if (error.status >= 500) {
            errorSubcategory = 'server-error';
            scope.setTag('errorType', 'server-error');
            scope.setLevel('error');
          } else if (error.status >= 400) {
            errorSubcategory = 'client-error';
            scope.setTag('errorType', 'client-error');
            scope.setLevel('warning');
          }
          scope.setTag('httpStatus', error.status.toString());
        } else if (error instanceof Error) {
          errorCategory = 'javascript';
          errorSubcategory = 'runtime-error';
          scope.setTag('errorType', 'javascript-runtime');
          scope.setLevel('error');
        }

        if (
          errorString.includes('NetworkError') ||
          errorString.includes('fetch')
        ) {
          errorCategory = 'network';
          errorSubcategory = 'network-error';
          scope.setTag('errorType', 'network');
          scope.setLevel('warning');
        }

        scope.setTag('errorCategory', errorCategory);
        scope.setTag('errorSubcategory', errorSubcategory);

        // Additional error details
        if (error instanceof Error) {
          scope.setContext('errorDetails', {
            name: error.name,
            message: error.message,
            stack: error.stack,
          });
        } else if (isErrorWithStatus(error)) {
          scope.setContext('errorDetails', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
          });
        }

        // Set user context if available (you might want to get this from your auth state)
        scope.setUser({
          id: currentUser?.id || '',
          email: currentUser?.email || '',
        });

        // Capture the error to Sentry
        if (isErrorWithStatus(error) && error.status === 404) {
          // For 404s, capture as a message rather than an exception
          Sentry.captureMessage(`404 Error: ${location.pathname}`, 'info');
        } else {
          // For other errors, capture as an exception
          Sentry.captureException(error);
        }
      });

      // Also log to console in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.group(`🚨 [ERROR ${errorId}] - ${timestamp}`);
        console.log('Raw error object:', error);
        console.log('Location:', location);
        console.log('Error logged to Sentry');
        console.groupEnd();
      }
    };

    logErrorToSentry();
  }, [currentUser?.id, error, location]);

  // A commit-phase DOM failure (in-page translation moving nodes out from under
  // React) leaves the previous page painted but detached from the fiber tree —
  // on top of the real UI and inert to every click. Nothing in-app can repair
  // that, so reload the document. Declared after the logging effect above so the
  // error reaches Sentry before we tear the page down. See ORA-262.
  useEffect(() => {
    if (!isUnrecoverableDomCommitError(error)) {
      return;
    }

    const storage = getDomCommitRecoveryStorage();
    const attempts = readDomCommitRecoveryAttempts(storage);

    if (attempts >= MAX_DOM_COMMIT_RECOVERY_ATTEMPTS) {
      addSentryBreadcrumb({
        message:
          'DOM commit failure recovery reload suppressed - attempt cap reached',
        level: 'warning',
        category: 'error-recovery',
        data: { pathname: location.pathname, attempts },
      });

      return;
    }

    // Only reload if the attempt is durably recorded. If storage is blocked the
    // counter resets on every load, so reloading would loop forever — leave the
    // user on the broken page instead, which at least terminates.
    if (!writeDomCommitRecoveryAttempts(storage, attempts + 1)) {
      addSentryBreadcrumb({
        message:
          'DOM commit failure recovery reload skipped - attempt counter unavailable',
        level: 'warning',
        category: 'error-recovery',
        data: { pathname: location.pathname },
      });

      return;
    }

    addSentryBreadcrumb({
      message: 'Reloading to recover from a DOM commit failure',
      level: 'warning',
      category: 'error-recovery',
      data: { pathname: location.pathname, attempt: attempts + 1 },
    });

    captureSentryMessage(
      'Unrecoverable DOM commit failure - reloading to recover',
      'warning'
    );

    const reloadTimeout = window.setTimeout(() => {
      window.location.reload();
    }, DOM_COMMIT_RECOVERY_RELOAD_DELAY_MS);

    return () => window.clearTimeout(reloadTimeout);
  }, [error, location.pathname]);

  useEffect(() => {
    const assetLoadFailure = classifyAssetLoadFailure(error);

    // A commit-phase DOM failure is owned by the effect above, which has its own
    // durable attempt cap. Letting this one fire too would stack two independent
    // reload budgets on the same error.
    const shouldAttemptAutoRecovery =
      !isUnrecoverableDomCommitError(error) &&
      ((isErrorWithStatus(error) && error.status !== 404) ||
        Boolean(assetLoadFailure));

    if (shouldAttemptAutoRecovery) {
      if (triggerGuardedClientReload()) {
        // Log recovery attempt to Sentry
        addSentryBreadcrumb({
          message: 'Attempting auto-recovery refresh',
          level: 'info',
          category: 'error-recovery',
        });
      } else {
        // Log that recovery was already attempted
        addSentryBreadcrumb({
          message: 'Auto-recovery already attempted, showing error page',
          level: 'warning',
          category: 'error-recovery',
        });
      }
    }
  }, [error]);

  const renderErrorPage = (error: unknown) => {
    const assetLoadFailure = classifyAssetLoadFailure(error);

    if (assetLoadFailure) {
      return <AssetLoadErrorView />;
    }

    if (isErrorWithStatus(error) && error.status === 404) {
      return (
        <div className="page-not-found">
          <h1>Page not found</h1>
          <p>Unfortunately, the page you are looking for cannot be found.</p>
          <Button
            className="!w-96"
            onClick={() => {
              // Log user interaction to Sentry
              addSentryBreadcrumb({
                message: 'User clicked "Return Home" from 404 page',
                level: 'info',
                category: 'user-interaction',
              });

              refreshAndReturnHome();
              hapticsImpactLight();
            }}
          >
            Return Home
          </Button>

          <div className="escape-hatch">
            <Button
              className="!pl-0"
              variant={'link'}
              onClick={() => {
                // Log escape hatch usage to Sentry
                addSentryBreadcrumb({
                  message:
                    'User clicked escape hatch from 404 page - clearing all data',
                  level: 'warning',
                  category: 'user-interaction',
                });

                escapeHatch();
                hapticsImpactLight();
              }}
            >
              Refresh not working? Clear data & log out
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="unexpected-error">
          <h1>Oops!</h1>
          <p>Sorry, an unexpected error has occurred.</p>
          <Button
            className="!w-96"
            onClick={() => {
              // Log user interaction to Sentry
              addSentryBreadcrumb({
                message:
                  'User clicked "Refresh & Return Home" - clearing app state',
                level: 'info',
                category: 'user-interaction',
              });

              refreshAndReturnHome();
              hapticsImpactLight();
            }}
          >
            Return Home
          </Button>

          <div className="escape-hatch">
            <Button
              className="!pl-0"
              variant={'link'}
              onClick={() => {
                // Log escape hatch usage to Sentry
                addSentryBreadcrumb({
                  message:
                    'User clicked escape hatch from error page - clearing all data',
                  level: 'warning',
                  category: 'user-interaction',
                });

                escapeHatch();
                hapticsImpactLight();
              }}
            >
              Not working? Hard refresh
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <div id="error-page" className="error-page">
      {renderErrorPage(error)}
    </div>
  );
}

export default ErrorPage;
